import React, { useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import TipWell from 'components/tip-well'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import CurrencyField from 'components/form-util/currency'
import AccountRouterLink from 'components/account-router-link'
import {
  isRecurringPayment,
  isWeeklyRecurringPayment,
  isMonthlyRecurringPayment,
} from 'utils/groupEventUtils'
import { centsToDollarsFormatted, dollarsToCents } from 'lib/utils/number'
import PaymentAccountWarning from 'components/payment-account-warning'
import ManageStripeAccountConfirmationDialog from 'pages/account/payments/payment-accounts/manage-stripe-account-confirmation-dialog'
import { useStyles as useFormStyles } from '../styles'

const priceLabel = event => {
  if (isWeeklyRecurringPayment(event)) {
    return 'Price (per week) *'
  }
  if (isMonthlyRecurringPayment(event)) {
    return 'Price (per month) *'
  }
  return 'Price (per person) *'
}

const feeLabel = paymentRecurrenceSchedule => (
  <Typography variant="body2">
    Please note that a 2.9% + $0.30 fee will be applied to{' '}
    {isRecurringPayment({ paymentRecurrenceSchedule }) ? (
      <>
        <strong>each</strong> billing cycle transaction.
      </>
    ) : (
      <>the transaction total.</>
    )}
  </Typography>
)

const Fees = ({ event, paymentAccounts }) => {
  const { errors, register, control, watch } = useFormContext()
  const formClasses = useFormStyles()
  const [isManageStripeDialogOpen, setIsManageStripeDialogOpen] = useState(
    false,
  )

  const price = watch('price')
  const totalPriceInCents = dollarsToCents(price) * event.numberOfPayments

  const selectedPaymentAccountId = watch('paymentAccountId')
  const selectedAccount = paymentAccounts.find(
    acc => acc.id === selectedPaymentAccountId,
  )
  const hasIncompleteStripeSetup =
    selectedAccount &&
    (!selectedAccount.detailsSubmitted || !selectedAccount.chargesEnabled)

  const allowsPaymentAccount =
    !isRecurringPayment(event) && !event.externalRegistrationUrl

  const priceDisabled = isRecurringPayment(event) && event.registrationCount > 0

  return (
    <>
      <Box className={formClasses.fieldGroup}>
        {priceDisabled ? (
          <Typography className={formClasses.fieldHelper}>
            The price of this subscription cannot be changed because one or more
            attendees have already registered.
          </Typography>
        ) : null}
        <CurrencyField
          name="price"
          label={priceLabel(event)}
          inputRef={register}
          error={!!errors.price}
          helperText={errors?.price?.message}
          readOnly={priceDisabled}
          className={formClasses.fieldInput}
        />
        {isRecurringPayment(event) ? (
          <Typography
            className={formClasses.fieldHelper}
            style={{ marginTop: 8 }}
          >
            Total cost: {centsToDollarsFormatted(totalPriceInCents)}
          </Typography>
        ) : null}
      </Box>
      {allowsPaymentAccount ? (
        <>
          <Box className={formClasses.fieldGroup}>
            <Typography className={formClasses.fieldHelper}>
              Select a payment account to collect registration fees online for
              this event. If no payment account is selected, upfront payments
              will not be required.
            </Typography>
            <FormControl
              error={!!errors.paymentAccountId || hasIncompleteStripeSetup}
              variant="outlined"
              fullWidth
              className={formClasses.fieldInput}
            >
              <InputLabel id="payment-account-label">
                Payment account
              </InputLabel>
              <Controller
                defaultValue=""
                as={
                  <Select
                    labelId="payment-account-label"
                    label="Payment account"
                    data-cy="paymentAccount"
                    value=""
                    defaultValue=""
                    disabled={isRecurringPayment(event)}
                  >
                    {paymentAccounts.map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                    <MenuItem key="none" value="">
                      <em>None</em>
                    </MenuItem>
                  </Select>
                }
                name="paymentAccountId"
                control={control}
              />
            </FormControl>
            <Box mt={1}>
              <Button
                color="primary"
                size="small"
                to="/account/payments/payment-accounts"
                component={AccountRouterLink}
              >
                Manage payment accounts
              </Button>
            </Box>
            <PaymentAccountWarning
              paymentAccount={selectedAccount}
              hasUnresolvedPaymentAccount={
                !!selectedPaymentAccountId && !selectedAccount
              }
              onOpen={() => setIsManageStripeDialogOpen(true)}
            />
            <Box mt={2}>
              <TipWell
                variant="neutral"
                icon={InfoOutlinedIcon}
                caption={feeLabel(event.paymentRecurrenceSchedule)}
              />
            </Box>
          </Box>
        </>
      ) : null}
      <ManageStripeAccountConfirmationDialog
        paymentAccount={selectedAccount}
        isOpen={isManageStripeDialogOpen}
        onClose={() => setIsManageStripeDialogOpen(false)}
      />
    </>
  )
}

export default Fees
